import React, { useCallback, useMemo, useState } from 'react';
import { useField } from 'formik';

import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/toast';

import {
  buildDataExtractionJsonSchema,
  createEmptyDataExtractionProperty,
  type DataExtractionPropertyFormValue,
  type DataExtractionSchemaMode,
  isDataExtractionBuilderCompatible,
  isDataExtractionRootArraySchema,
  mapJsonSchemaToDataExtractionProperties,
  parseDataExtractionJsonSchema,
  parseJsonEditorValue,
  stringifyDataExtractionJsonSchema,
} from '@/lib/data-extraction';

import { getEnumValidationMessage, type PropertyDraftErrors } from './data-extraction-prompt.utils';
import { DataExtractionPropertiesList } from './data-extraction-properties-list';
import { DataExtractionPropertyDialog } from './data-extraction-property-dialog';
import { PostCallPrompt } from './post-call-prompt';

type DataExtractionDialogMode = 'manual' | 'json';

type DataExtractionPromptProps = {
  promptVariables: string[];
};

export const DataExtractionPrompt = React.memo(({ promptVariables }: DataExtractionPromptProps) => {
  const [schemaModeField, , schemaModeHelpers] = useField<DataExtractionSchemaMode>(
    'data_extraction_schema_mode'
  );
  const [jsonSchemaField, jsonSchemaMeta, jsonSchemaHelpers] = useField<string>(
    'data_extraction_json_schema'
  );
  const [rootIsArrayField, , rootIsArrayHelpers] = useField<boolean>(
    'data_extraction_root_is_array'
  );
  const [propertiesField, propertiesMeta, propertiesHelpers] = useField<
    DataExtractionPropertyFormValue[]
  >('data_extraction_properties');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DataExtractionDialogMode>('manual');
  const [draft, setDraft] = useState<DataExtractionPropertyFormValue>(() =>
    createEmptyDataExtractionProperty()
  );
  const [jsonDraft, setJsonDraft] = useState('');
  const [selectedJsonTemplate, setSelectedJsonTemplate] = useState<string | null>(null);
  const [draftErrors, setDraftErrors] = useState<PropertyDraftErrors>({});

  const schemaMode = schemaModeField.value ?? 'builder';
  const rawJsonSchema = jsonSchemaField.value ?? '';
  const rootIsArray = rootIsArrayField.value ?? false;
  const properties = propertiesField.value ?? [];
  const generatedSchema = useMemo(
    () => buildDataExtractionJsonSchema(properties, { rootIsArray }),
    [properties, rootIsArray]
  );
  const arrayError = typeof propertiesMeta.error === 'string' ? propertiesMeta.error : undefined;
  const parsedJsonSchema = useMemo(
    () => parseDataExtractionJsonSchema(rawJsonSchema),
    [rawJsonSchema]
  );
  const jsonSchemaError =
    schemaMode === 'json'
      ? typeof jsonSchemaMeta.error === 'string'
        ? jsonSchemaMeta.error
        : parsedJsonSchema.error
      : undefined;
  const canSwitchToBuilder =
    !parsedJsonSchema.schema || isDataExtractionBuilderCompatible(parsedJsonSchema.schema);
  const dialogJsonState = useMemo(() => parseJsonEditorValue(jsonDraft), [jsonDraft]);
  const dialogJsonError =
    typeof jsonSchemaMeta.error === 'string' && !dialogJsonState.isTemplate
      ? jsonSchemaMeta.error
      : dialogJsonState.error;
  const canUseManualMode =
    !dialogJsonState.schema || isDataExtractionBuilderCompatible(dialogJsonState.schema);

  const updateBuilderSchema = useCallback(
    (nextProperties: DataExtractionPropertyFormValue[], nextRootIsArray = rootIsArray) => {
      jsonSchemaHelpers.setValue(
        stringifyDataExtractionJsonSchema(
          buildDataExtractionJsonSchema(nextProperties, {
            rootIsArray: nextRootIsArray,
          })
        )
      );
    },
    [jsonSchemaHelpers, rootIsArray]
  );

  const openCreateDialog = useCallback(() => {
    setDraft(createEmptyDataExtractionProperty());
    setDraftErrors({});
    setDialogMode(schemaMode === 'json' ? 'json' : 'manual');
    setSelectedJsonTemplate(null);
    setJsonDraft(
      schemaMode === 'json' ? rawJsonSchema : stringifyDataExtractionJsonSchema(generatedSchema)
    );
    setIsDialogOpen(true);
  }, [generatedSchema, rawJsonSchema, schemaMode]);

  const openEditDialog = useCallback(
    (property: DataExtractionPropertyFormValue) => {
      setDraft({
        ...property,
        enumValues: [...property.enumValues],
      });
      setDraftErrors({});
      setDialogMode('manual');
      setSelectedJsonTemplate(null);
      setJsonDraft(stringifyDataExtractionJsonSchema(generatedSchema));
      setIsDialogOpen(true);
    },
    [generatedSchema]
  );

  const removeProperty = useCallback(
    (propertyId: string) => {
      const nextProperties = properties.filter(property => property.id !== propertyId);

      propertiesHelpers.setValue(nextProperties);
      updateBuilderSchema(nextProperties);
      toast.success('Property deleted');
    },
    [properties, propertiesHelpers, updateBuilderSchema]
  );

  const closeDialog = useCallback((open: boolean) => {
    setIsDialogOpen(open);

    if (!open) {
      setDraftErrors({});
      setSelectedJsonTemplate(null);
    }
  }, []);

  const onDialogModeChange = useCallback(
    (nextMode: DataExtractionDialogMode) => {
      if (nextMode === 'manual' && !canUseManualMode) {
        toast.info(
          'The current JSON schema uses structures that the field builder cannot represent.'
        );
        return;
      }

      setDialogMode(nextMode);
    },
    [canUseManualMode]
  );

  const applyJsonSchema = useCallback(() => {
    if (dialogJsonError) {
      toast.error(dialogJsonError);
      return;
    }

    const nextSchema = dialogJsonState.schema;
    const nextRootIsArray = isDataExtractionRootArraySchema(nextSchema);
    const canUseBuilderSchema = nextSchema && isDataExtractionBuilderCompatible(nextSchema);

    jsonSchemaHelpers.setValue(
      nextSchema ? stringifyDataExtractionJsonSchema(nextSchema) : jsonDraft
    );
    schemaModeHelpers.setValue('json');
    rootIsArrayHelpers.setValue(nextRootIsArray);
    propertiesHelpers.setValue(canUseBuilderSchema ? dialogJsonState.properties : []);
    setDraftErrors({});
    setIsDialogOpen(false);
    toast.success('JSON schema updated');
  }, [
    dialogJsonError,
    dialogJsonState.properties,
    dialogJsonState.schema,
    jsonDraft,
    jsonSchemaHelpers,
    propertiesHelpers,
    rootIsArrayHelpers,
    schemaModeHelpers,
  ]);

  const saveProperty = useCallback(() => {
    const identifier = draft.identifier.trim();
    const normalizedEnumValues = draft.enumValues.map(value => value.trim()).filter(Boolean);
    const nextErrors: PropertyDraftErrors = {};
    const builderCompatibleDialogSchema =
      dialogJsonState.schema && isDataExtractionBuilderCompatible(dialogJsonState.schema)
        ? dialogJsonState.schema
        : undefined;
    const baseProperties =
      !isDialogOpen || properties.some(property => property.id === draft.id)
        ? properties
        : builderCompatibleDialogSchema
          ? mapJsonSchemaToDataExtractionProperties(builderCompatibleDialogSchema)
          : properties;
    const nextRootIsArray = builderCompatibleDialogSchema
      ? isDataExtractionRootArraySchema(builderCompatibleDialogSchema)
      : rootIsArray;

    if (!identifier) {
      nextErrors.identifier = 'Property name is required';
    }

    if (
      baseProperties.some(
        property =>
          property.id !== draft.id &&
          property.identifier.trim().toLowerCase() === identifier.toLowerCase()
      )
    ) {
      nextErrors.identifier = 'Property name must be unique';
    }

    if (draft.dataType === 'array' && !draft.arrayItemType) {
      nextErrors.arrayItemType = 'Array item type is required';
    }

    const enumError = getEnumValidationMessage(normalizedEnumValues, draft);

    if (enumError) {
      nextErrors.enumValues = enumError;
    }

    if (Object.keys(nextErrors).length > 0) {
      setDraftErrors(nextErrors);
      return;
    }

    const nextProperty: DataExtractionPropertyFormValue = {
      ...draft,
      identifier,
      description: draft.description.trim(),
      enumValues: normalizedEnumValues,
      arrayItemType: draft.dataType === 'array' ? (draft.arrayItemType ?? 'string') : null,
    };
    const existingIndex = baseProperties.findIndex(property => property.id === draft.id);
    let nextProperties = baseProperties;

    if (existingIndex >= 0) {
      nextProperties = [...baseProperties];
      nextProperties[existingIndex] = nextProperty;
      toast.success('Property updated');
    } else {
      nextProperties = [...baseProperties, nextProperty];
      toast.success('Property added');
    }

    propertiesHelpers.setValue(nextProperties);
    rootIsArrayHelpers.setValue(nextRootIsArray);
    updateBuilderSchema(nextProperties, nextRootIsArray);
    schemaModeHelpers.setValue('builder');

    setDraftErrors({});
    setIsDialogOpen(false);
  }, [
    dialogJsonState.schema,
    draft,
    isDialogOpen,
    properties,
    propertiesHelpers,
    rootIsArray,
    rootIsArrayHelpers,
    schemaModeHelpers,
    updateBuilderSchema,
  ]);

  return (
    <>
      <PostCallPrompt
        title="Data extraction"
        description="Define custom data specifications to extract from conversation transcripts."
        name="data_extraction_prompt"
        variablesName="data_extraction_prompt_variables"
        promptVariables={promptVariables}
        inputId="data-extraction-prompt"
        promptDescription="This is the prompt that's used to extract structured data from the call."
        promptPlaceholder="Enter the prompt for data extraction..."
        promptSectionClassName="border-b pb-6"
        textareaClassName="max-h-60"
      >
        <label className="flex items-start gap-3 rounded-lg border bg-muted/20 px-4 py-3">
          <Checkbox
            id="data-extraction-root-is-array"
            checked={rootIsArray}
            disabled={schemaMode === 'json'}
            onCheckedChange={checked => {
              const nextRootIsArray = Boolean(checked);

              rootIsArrayHelpers.setValue(nextRootIsArray);
              updateBuilderSchema(properties, nextRootIsArray);
              schemaModeHelpers.setValue('builder');
            }}
          />
          <div className="space-y-1">
            <span className="text-sm font-medium">Return an array of objects</span>
            <p className="text-sm text-muted-foreground">
              Wrap the generated schema as an array whose items follow the field definitions below.
            </p>
          </div>
        </label>

        <DataExtractionPropertiesList
          properties={properties}
          arrayError={arrayError}
          addButtonLabel={schemaMode === 'json' ? 'Open editor' : 'Add field'}
          helperText={
            schemaMode === 'json'
              ? canSwitchToBuilder
                ? 'JSON schema mode is active. Open the dialog to edit the schema or switch back to manual fields.'
                : 'JSON schema mode is active. This schema can only be edited in JSON mode.'
              : undefined
          }
          readOnly={schemaMode === 'json'}
          emptyStateMessage={
            schemaMode === 'json'
              ? 'No field preview is available. Open the dialog to edit the active JSON schema.'
              : undefined
          }
          onAddProperty={openCreateDialog}
          onEditProperty={openEditDialog}
          onRemoveProperty={removeProperty}
        />

        {jsonSchemaError && <p className="text-sm text-destructive">{jsonSchemaError}</p>}
      </PostCallPrompt>

      <DataExtractionPropertyDialog
        open={isDialogOpen}
        isEditing={properties.some(property => property.id === draft.id)}
        mode={dialogMode}
        canUseManualMode={canUseManualMode}
        draft={draft}
        draftErrors={draftErrors}
        jsonValue={jsonDraft}
        jsonError={dialogJsonError}
        selectedJsonTemplate={selectedJsonTemplate}
        setDraft={setDraft}
        onModeChange={onDialogModeChange}
        onJsonChange={value => {
          setSelectedJsonTemplate(null);
          setJsonDraft(value);
        }}
        onSelectJsonTemplate={(templateKey, templateValue) => {
          setSelectedJsonTemplate(templateKey);
          setJsonDraft(templateValue);
        }}
        onOpenChange={closeDialog}
        onSave={saveProperty}
        onApplyJson={applyJsonSchema}
      />
    </>
  );
});
